// Standard library imports
import React, { useContext, useEffect, useState } from 'react';

// External library imports
import { useLocation } from 'react-router-dom';
import { UilSync } from '@iconscout/react-unicons';

// Internal module imports
import Typography from '../../../components/Typography/Typography';
import Table from '../../../components/Table/Table';
import { DownloadAsExcel } from '../../../utils/downloadAsExcel';
import DownloadButton from '../../../components/Buttons/DownloadButton';
import CustomTooltip from '../../../components/CustomToolTip/CustomTooltip';
import { momentTimeFormater } from '../../../utils/dateHelper';
import { DropdownComponent } from '../../../components/Inputs/Input';

// Css module imports
import classes from '../../../styles/AllDevices.module.css';
import myClasses from '../SmartCharging.module.css';
import { SCService } from '../../../services/SCService';
import _ from 'lodash';
import { useLoader } from '../../../hooks';
import { toast } from 'react-toastify';
import { AuthContext } from '../../../context/AuthContext';

const defaultValue = {
    label: 'All',
    value: 'All',
};

const LIMIT = 15;

const AllChargers = ({ devices, updateQueryParams, queryParamsData, sites = [] }) => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    const [startLoader, stopLoader] = useLoader();
    const { state } = useContext(AuthContext);
    const customer = _.get(state, 'user.customerId');
    const licenseTypeId = state.isAuthorized ? queryParams.get('licenseTypeId') : '';

    const [tableData, setTableData] = useState([]);
    const [chargesData, setCharges] = useState([]);
    const [groups, setGroups] = useState([defaultValue]);
    const [selectedGroup, setSelectedGroup] = useState(queryParamsData.group ? queryParamsData.group : defaultValue);
    const [skip, setSkip] = useState(0);
    const [page, setPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        getChargers();
    }, [selectedGroup.value, page]);

    useEffect(() => {
        updateQueryParams('group', JSON.stringify(selectedGroup));
    }, [selectedGroup]);

    useEffect(() => {
        setGroups([defaultValue, ...sites]);
    }, [sites]);

    const getChargers = () => {
        if (customer?._id && licenseTypeId) {
            SCService.GetChargers(
                {
                    limit: LIMIT,
                    skip,
                    customerId: customer._id,
                    licenseTypeId,
                    groupId: selectedGroup.value,
                },
                () => startLoader('getChargers'),
                handleGetChargerSuccess,
                handleError,
                () => stopLoader('getChargers')
            );
        }
    };

    const handleError = (err) => {
        let data = err && err.response ? err.response.data : null;
        if (data) toast.error(data.message);
        else toast.error('Internal server error!');
    };

    const handleGetChargerSuccess = ({ data }) => {
        const { chargers, totalCount } = data?.data;
        const processedData = chargers.map((instance, index) => {
            return {
                's.no': index + 1 + skip,
                name: instance.name,
                description: !instance?.description ? (
                    '--'
                ) : instance.description.length > 40 ? (
                    <CustomTooltip content={instance.description}>{instance.description.substring(0, 40) + '...'}</CustomTooltip>
                ) : (
                    instance.description
                ),
                evseId: instance?.meta?.evseId ? instance.meta.evseId : '--',
                connectors: instance?.meta?.connectors ? instance.meta.connectors : '--',
                active: instance?.meta?.isActive ? instance.meta.isActive : false,
                id: instance._id,
                createdAt: instance.createdAt ? momentTimeFormater(instance.createdAt).format('YYYY-MM-DD HH:mm:ss') : '--',
                smartCharging: instance.meta?.sendOCPIChargingProfile === true ? instance.meta.sendOCPIChargingProfile : false,
                latestSessionId: instance.sessionId || '--',
                latestSoc: instance.latestSOC || '--'
            };
        });
        setCharges(chargers);
        setTableData(processedData);
        setTotalCount(totalCount);
    };

    const handleExport = () => {
        const processedData = [];
        chargesData.map((instance) => {
            processedData.push({
                name: instance.name,
                description: instance.description,
                evseId: instance?.meta?.evseId,
                connectors: instance?.meta?.connectors,
                smartCharging: instance.meta?.sendOCPIChargingProfile ? instance.meta.sendOCPIChargingProfile : false,
                latestSessionId: instance.sessionId || '--',
                latestSoc: instance.latestSOC || '--',
                createdAt: instance.createdAt ? momentTimeFormater(instance.createdAt).format('YYYY-MM-DD HH:mm:ss') : '',
            });
        });
        DownloadAsExcel(processedData, 'Smart_Charging_Chargers', ['Name', 'Description', 'Charger ID', 'Connectors', 'Smart Charging', 'Last Session ID', 'Last Reported SOC', 'Created At (CET)']);
    };

    const handlePageChange = (pageno) => {
        setSkip(LIMIT * pageno);
        setPage(pageno);
    };

    return (
        <div className={classes.Ventilation + ' ventilation'}>
            <div className={myClasses.FilterContainer}>
                <div className={myClasses.Header}>
                    <div className={classes.FieldControl2}>
                        <label>Site</label>
                        <DropdownComponent name="groupId" options={groups} defaultValue={selectedGroup} onChange={(e) => setSelectedGroup(e)} />
                    </div>
                </div>
            </div>
            <div className={classes.Alert}>
                <div className={classes.Header}>
                    <div>
                        <Typography content="All Chargers" />
                        <div className={classes.TableCount}>
                            Total Count :
                            <span>
                                <Typography size="14" content={devices.length} />
                            </span>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'end', justifyContent: 'center', gap: '1vw' }}>
                        <div>
                            <DownloadButton size="medium" onClick={() => handleExport()} />
                        </div>
                        <div onClick={() => getChargers()}>
                            <UilSync style={{ color: 'var(--color-primary)', cursor: 'pointer' }} size="1.2vw" />
                        </div>
                    </div>
                </div>
                <div>
                    <Table
                        head={['S.No', 'Name', 'Description', 'CHARGER ID', 'Connectors', 'Smart Charging', 'Last Session Id', 'Last Reported SOC', 'Created At (CET)',
                        ]}
                        keys={['s.no', 'name', 'description', 'evseId', 'connectors', 'smartCharging', 'latestSessionId', 'latestSoc', 'createdAt',
                        ]}
                        data={tableData}
                        page={page}
                        Pagination={true}
                        limit={LIMIT}
                        handlePageChange={handlePageChange}
                        totalCount={totalCount}
                    />
                </div>
            </div>
        </div>
    );
};

export default AllChargers;
